class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.key === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.layoutController = new AbortController();
    const signal = this.layoutController.signal;
    this.refreshCartLayout = () => {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const body = this.querySelector('.drawer__body');
        const hint = this.querySelector('[data-cart-more]');
        if (body !== this.scrollBody) {
          this.scrollBody?.removeEventListener('scroll', this.refreshCartLayout);
          this.layoutResize?.disconnect();
          this.scrollBody = body;
          body?.addEventListener('scroll', this.refreshCartLayout, {passive:true, signal});
          if (body && 'ResizeObserver' in window) {
            this.layoutResize = new ResizeObserver(this.refreshCartLayout);
            this.layoutResize.observe(body);
            const form = body.querySelector('form');
            if (form) this.layoutResize.observe(form);
          }
        }
        if (hint && body) hint.hidden = this.classList.contains('is-empty') || body.scrollHeight - body.clientHeight - body.scrollTop < 8;
      });
    };
    this.viewportChanged = () => {
      if (this.classList.contains('active')) this.style.setProperty('--gm-cart-viewport', `${window.visualViewport?.height || window.innerHeight}px`);
      this.refreshCartLayout();
    };
    this.layoutMutation = new MutationObserver(this.refreshCartLayout);
    this.layoutMutation.observe(this, {childList:true, subtree:true});
    this.openMutation = new MutationObserver(this.viewportChanged);
    this.openMutation.observe(this, {attributes:true, attributeFilter:['class']});
    this.addEventListener('click', event => {
      if (!event.target.closest('[data-cart-more]')) return;
      this.scrollBody?.scrollBy({top:Math.max(120, this.scrollBody.clientHeight * .7), behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
    }, {signal});
    this.addEventListener('toggle', this.refreshCartLayout, {capture:true, signal});
    window.addEventListener('resize', this.viewportChanged, {signal});
    window.visualViewport?.addEventListener('resize', this.viewportChanged, {signal});
    this.refreshCartLayout();
  }

  disconnectedCallback() {
    this.layoutController?.abort();
    this.layoutMutation?.disconnect();
    this.openMutation?.disconnect();
    this.layoutResize?.disconnect();
    cancelAnimationFrame(this.layoutFrame);
    clearTimeout(this.openTimer);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
		if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.viewportChanged?.();
      const body = this.querySelector('.drawer__body');
      if (body) body.scrollTop = 0;
      const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.querySelector('.drawer__inner');
      const first = container?.querySelector('.drawer__close') || container;
      if (container && first) trapFocus(container, first);
    }, 0);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
